import { useCallback, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { useLocalStorageState } from '@components/component-helpers';
import { Columns, ColumnSettings, DataGrid } from '@components/data-grid';
import { RowAction } from '@components/data-grid/data-grid-types';
import { EmptyState } from '@components/empty-state';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconDepartment } from '@components/icons/department';
import { IconEdit } from '@components/icons/edit';
import { IconJob } from '@components/icons/job';
import { IconPersonAdd } from '@components/icons/person-add';
import { IconTeam } from '@components/icons/team';
import { IconTrash } from '@components/icons/trash';
import { Text } from '@components/text';
import { sortByTitle } from '@utils/sort-by-title';
import { getTitleLanguage, getTranslatedTitle } from '@utils/translated-title';

import { api } from 'api';
import { Department } from 'api/departments/requests';
import { Header } from 'components/headers';
import { Loading } from 'components/Loading';
import { PageContent } from 'components/Page';
import { useModal } from 'features/modals';
import { usePermissions } from 'features/permissions';
import { useStore } from 'models/Provider';

export const Departments = () => {
  const { t } = useStore();
  const { showModal } = useModal();
  const { hasRolePermission } = usePermissions();

  return (
    <>
      <Header
        // @ts-ignore
        breadcrumbs={<Breadcrumb items={[{ label: t('company.settings.departments.header') }]} />}
        actions={
          <Button
            text={t('company.settings.departments.add')}
            iconLeft={<IconAddPlus />}
            isDisabled={!hasRolePermission('departments:add')}
            tooltip={
              hasRolePermission('departments:add')
                ? undefined
                : t('company.settings.departments.add.disabled')
            }
            onClick={() => showModal('ADD_DEPARTMENT')}
          />
        }
      />
      <PageContent noPadding>
        <DepartmentList />
      </PageContent>
    </>
  );
};

const DepartmentList = () => {
  const { current, t, lang } = useStore();
  const noDepartmentsInfoQuery = useQuery({
    queryKey: ['departments', 'no-info'],
    queryFn: api.departments.noDepartmentsInfo,
  });
  const departmentsQuery = useQuery({
    queryKey: ['departments', 'list'],
    queryFn: api.departments.list,
    enabled: noDepartmentsInfoQuery.isSuccess,
    select: useCallback(
      (data: Department[]) => {
        const departments = sortByTitle<Department & { title: string }>(
          data.map((department: Department) => ({
            id: department.id,
            externalId: department.externalId,
            title:
              getTranslatedTitle(department.translations, lang, current.info.defaultLanguage) ?? '',
            translations: department.translations,
            numJobs: department.numJobs,
            numUsers: department.numUsers,
            users: department.users,
            hasAccess: department.hasAccess,
            canEditFullAccess: department.canEditFullAccess,
          })),
          ({ title }) => title,
          getTitleLanguage(
            data.map(({ translations }: Department) => translations),
            lang,
            current.info.defaultLanguage,
          ),
        );
        return [
          {
            id: -1,
            title: t('company.settings.departments.nodepartment'),
            numJobs: noDepartmentsInfoQuery.data?.jobs || 0,
            numUsers: noDepartmentsInfoQuery.data?.users || 0,
            externalId: '',
            translations: [],
            users: [],
            hasAccess: false,
            canEditFullAccess: false,
          },
          ...departments,
        ];
      },
      [
        current.info.defaultLanguage,
        lang,
        noDepartmentsInfoQuery.data?.jobs,
        noDepartmentsInfoQuery.data?.users,
        t,
      ],
    ),
  });
  const actionsForDepartment = useDepartmentOptions();
  const [columns] = useState<Columns<Department & { title: string }>>([
    {
      key: 'title',
      label: t('company.settings.departments.columns.title'),
      render: (department) => {
        return (
          <Text
            font="regular14"
            color={department.id === -1 ? 'purple50' : 'purple100'}
            text={department.title}
          />
        );
      },
    },
    {
      key: 'externalId',
      label: t('company.settings.departments.columns.externalid'),
      render: (department) =>
        department.externalId ? (
          <>{department.externalId}</>
        ) : department.id === -1 ? (
          <>-</>
        ) : (
          <Text
            font="regular14"
            color="purple50"
            text={t('company.settings.departments.columns.externalid.none')}
            style={{ display: 'inline' }}
          />
        ),
    },
    {
      key: 'numJobs',
      labelIcon: IconJob,
      type: 'numeric',
      center: true,
      ratio: 0.1,
      label: t('company.settings.departments.columns.numjobs'),
    },
    {
      key: 'numUsers',
      labelIcon: IconTeam,
      type: 'numeric',
      ratio: 0.1,
      center: true,
      label: t('company.settings.departments.columns.numusers'),
    },
  ]);

  const [settings, setSettings] = useLocalStorageState<
    ColumnSettings<Department & { title: string }>
  >({
    key: 'departments-columns',
    defaultValue: {
      title: { order: 0, visible: true },
      externalId: { order: 1, visible: false },
      numJobs: { order: 2, visible: true },
      numUsers: { order: 3, visible: true },
    },
  });

  if (noDepartmentsInfoQuery.isLoading || departmentsQuery.isLoading || !departmentsQuery.data) {
    return <Loading />;
  }
  if (departmentsQuery.data.length === 0) {
    return (
      <EmptyState
        icon={<IconDepartment size={80} />}
        header={t('company.settings.departments.nodepartments.header')}
        content={t('company.settings.departments.nodepartments.content')}
      />
    );
  }

  return (
    <DataGrid
      label="Departments"
      data={departmentsQuery.data}
      columnSettings={settings}
      onColumnSettingsChange={setSettings}
      columns={columns}
      actions={actionsForDepartment}
    />
  );
};

function useDepartmentOptions() {
  const { t } = useStore();
  const { showModal } = useModal();
  const { hasRolePermission } = usePermissions();
  return useCallback(
    (department) => {
      if (department.id === -1) {
        return [];
      }
      let options: Array<RowAction<Department>> = [
        {
          label: t('company.settings.departments.actions.addusers'),
          icon: <IconPersonAdd />,
          isDisabled: () => !hasRolePermission('departments:add-user'),
          onSelect: (department) => {
            showModal('ADD_DEPARTMENT_USER', {
              departmentId: department.id,
              departmentTitle: department.title ?? '',
              departmentUserIds: department.users,
              canEditFullAccess: department.canEditFullAccess,
            });
          },
        },
      ];
      if (hasRolePermission('departments:edit')) {
        options.push({
          label: t('company.settings.departments.actions.edit'),
          icon: <IconEdit />,
          onSelect: (department) =>
            showModal('EDIT_DEPARTMENT', {
              departmentId: department.id,
              externalId: department.externalId,
              translations: department.translations,
            }),
        });
      }
      if (hasRolePermission('departments:edit')) {
        options.push({
          label: t('company.settings.departments.actions.delete'),
          icon: <IconTrash />,
          variant: 'danger',
          isDisabled: (department) => department.numJobs > 0,
          tooltip: (department) =>
            department.numJobs > 0
              ? t('company.settings.departments.actions.delete.disabled')
              : undefined,
          onSelect: (department) => showModal('DELETE_DEPARTMENT', { departmentId: department.id }),
        });
      }
      return options;
    },
    [hasRolePermission, t, showModal],
  );
}
